import React, { useEffect, useState } from "react"
import L10n from "lib/carrot-l10n"
import { reportErrorMessage } from "utils/ErrorReporting"
import { CardExpenseStatus, ExpenseStatus } from "utils/ExpenseStatuses"
import { getCardState, getIsCarrotCardEligible } from "reducers/userInfo"
import { Spacer } from "components/views/atoms/Atoms"
import { useSelector } from "react-redux"
import CardStates from "utils/CardStates"
import { MultiPathSVG, SinglePathSVG } from "components/views/SVG"
import { getReimbursementsList } from "reducers/reimbursements"
import { Body, BodySmall } from "components/views/atoms/Bodies"
import { TextLink } from "components/views/atoms/links/TextLink"
import { CircleImage } from "components/views/atoms/images/CircleImage"
import Settings from "utils/CarrotConfig"
import { ReimbursementsListPaidStatus } from "components/views/reimbursements/reimbursementsList/ReimbursementsListPaidStatus"
import { ReimbursementsListDeductibleString } from "components/views/reimbursements/reimbursementsList/ReimbursementsListDeductibleString"
import { useToggle } from "../../../../utils/Hooks"
import { Container, FlexContainer, H2, H3, H4, H5, Icon, Text } from "@carrotfertility/carotene"
import { GetReimbursed } from "components/get-reimbursed"
import { Modal } from "components/views/modal/Modal"
import { getSubsidies } from "../../../../reducers/subsidies"
import styled from "styled-components"
import { AccordionWithoutMarkdown } from "../../Accordion"
import { RetinaImage } from "../../atoms/images/RetinaImage"
import Badge from "./Badge"
import { PlanInfoBadgeContainer } from "../../../containers/planinfo/PlanInfoBadgeContainer"
import { StylelessButton } from "../../molecules/Molecules"
import { DbgPaths } from "../../../../utils/Paths"
import { ReimbursementDescriptionGroup, ReimbursementDescriptionList } from "./ReimbursementDescriptionList"
import { FormattedMessage, useIntl, FormattedNumber, FormattedDate } from "react-intl"
import EmployeeOnlyModalContainer from "../../../employee-only-containers/EmployeeOnlyModalContainer"
import { getPiiProps } from "services/tracking"
import { getCompanyCurrencyCode } from "../../../../reducers/companyInfo"
import moment from "moment"
import { reimbursementCategoryNameMessages } from "../reimbursementCategoryMessages"
import { reimbursementCategoryNameMessageKeys } from "../reimbursementCategoryNameMessageKeys"
import { useLocation } from "react-router-dom"

const l = new L10n({ locale: "en-US" }).getBoundLocalize()

const CategoryTitle = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-right: 17px;
`

const CategoryHeader = styled.div`
  display: flex;
  flex-direction: row;
`

const CategoryIconColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  width: 60px;
`

const CategoryAccordionColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  width: 100%;
`

const CategoryDivider = styled.div`
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  margin: 0.5rem 0;
`

const getReimbursementsRequiringActionList = (reimbursements) => {
  return reimbursements
    ? reimbursements.filter(
        (x) =>
          x.isCardExpense &&
          (x.cardStatus === CardExpenseStatus.DENIED_RECONCILIATION_REQUESTED ||
            x.cardStatus === CardExpenseStatus.PENDING_MEMBER_INFO)
      )
    : []
}

const getPendingReimbursementsList = (reimbursements) => {
  return reimbursements
    ? reimbursements.filter(
        (x) =>
          (!x.isCardExpense && (x.status === ExpenseStatus.RECEIVED || x.status === ExpenseStatus.APPROVED)) ||
          (x.isCardExpense && x.cardStatus === CardExpenseStatus.PENDING_CARE_NAV)
      )
    : []
}

const getCategoryPendingReimbursementsList = (reimbursements) => {
  return reimbursements
    ? reimbursements.filter(
        (x) =>
          (!x.isCardExpense &&
            ((x.status === ExpenseStatus.RECEIVED && x.hasLineItems) || x.status === ExpenseStatus.APPROVED)) ||
          (x.isCardExpense && x.cardStatus === CardExpenseStatus.PENDING_CARE_NAV)
      )
    : []
}

const getReceivedReimbursementsList = (reimbursements) => {
  return reimbursements
    ? reimbursements.filter((x) => !x.isCardExpense && x.status === ExpenseStatus.RECEIVED && !x.hasLineItems)
    : []
}

const getCompletedReimbursementsList = (reimbursements) => {
  return reimbursements
    ? reimbursements.filter(
        (x) =>
          (!x.isCardExpense && (x.status === ExpenseStatus.DENIED || x.status === ExpenseStatus.PAID)) ||
          (x.isCardExpense &&
            (x.cardStatus === ExpenseStatus.APPROVED || x.cardStatus === CardExpenseStatus.DENIED_RECONCILED))
      )
    : []
}

const FileDrawingLink = ({ guid }) => (
  <a
    className="mr1 carrot-orange hover-dark-carrot-orange animate-all"
    href={`${Settings.CARROT_BACKEND_URL}/api/v2/statements/${guid}`}
    target="_blank"
    rel="noreferrer"
  >
    <SinglePathSVG icon="statementFile" />
  </a>
)

const FilePendingScan = () => {
  const [showPendingScanModal, togglePendingScanModal] = useToggle()
  const PendingScanModal = () => (
    <EmployeeOnlyModalContainer onClose={togglePendingScanModal}>
      <Modal
        titleText={l`client.reimbursements.adjustments.whatAdjustedMeans`}
        onExit={togglePendingScanModal}
        underlayClickExits={true}
      >
        <Container paddingLeftRight="huge" alignItems="center">
          <H2>{l`client.reimbursements.adjustments.fileUnavailable`}</H2>
          <Spacer height={1.5} />
          <BodySmall>{l`client.reimbursements.adjustments.scanningFile`}</BodySmall>
          <Spacer height={3} />
        </Container>
      </Modal>
    </EmployeeOnlyModalContainer>
  )
  return (
    <>
      <StylelessButton
        className="mr1 carrot-orange hover-dark-carrot-orange animate-all"
        onClick={togglePendingScanModal}
      >
        <SinglePathSVG icon="statementFile" />
      </StylelessButton>
      {showPendingScanModal && <PendingScanModal onExit={togglePendingScanModal} />}
    </>
  )
}

const Header = ({ children, rightAlign = false }) => (
  <th className="black-60 gotham ttu tl medium" style={{ fontSize: "11px" }}>
    <div className={`mb2 ${rightAlign ? "tr" : null}`}>{children}</div>
  </th>
)

const Legend = () => {
  const isCarrotCardEligible = useSelector(getIsCarrotCardEligible)
  const hasHadCarrotCardExp = useSelector(getReimbursementsList)
  const [hasPrevCarrotCardExpenses, setHasPrevCarrotCardExpenses] = useState(false)

  useEffect(() => {
    hasHadCarrotCardExp.forEach((x) => {
      if (x.isCardExpense) {
        if (!hasPrevCarrotCardExpenses) setHasPrevCarrotCardExpenses(true)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- See https://carrotfertility.atlassian.net/wiki/spaces/PE/pages/2050295461/Remove+Build+Warnings#react-hooks%2Fexhaustive-deps
  }, [])

  return (
    <div className="fr tr">
      {(isCarrotCardEligible || hasPrevCarrotCardExpenses) && (
        <BodySmall>
          <span>
            <FormattedMessage defaultMessage="Carrot Card®" />
          </span>
          <MultiPathSVG iconName="icn-carrot-card-key" className="dib ml2" />
        </BodySmall>
      )}
      <BodySmall>
        <FormattedMessage defaultMessage="Out of pocket" description="money paid upfront" />
        <MultiPathSVG iconName="icn-oop-key" className="dib ml2" />
      </BodySmall>
    </div>
  )
}

const Attachments = ({ reimbursement }) => {
  const queryParams = new URLSearchParams(useLocation().search)
  const enableUpload =
    reimbursement.cardStatus === CardExpenseStatus.PENDING_MEMBER_INFO ||
    reimbursement.cardStatus === CardExpenseStatus.DENIED_RECONCILIATION_REQUESTED

  const expenseId = queryParams.get("upload-expense-id")
  const isModalInitiallyOpen = expenseId == reimbursement.expenseId
  const [isReimbursementsModalOpen, toggleIsReimbursementsModalOpen] = useToggle(isModalInitiallyOpen)

  if (enableUpload) {
    return (
      <>
        <Text
          className="carrot-orange hover-dark-carrot-orange animate-all"
          onClick={() => toggleIsReimbursementsModalOpen()}
          sx={{ cursor: "pointer" }}
        >
          <MultiPathSVG iconName="btn-upload" />
        </Text>
        {isReimbursementsModalOpen && (
          <GetReimbursed
            expenseId={reimbursement.expenseId}
            transDate={reimbursement.submittedDatetime}
            transAmount={reimbursement.amount}
            onExit={toggleIsReimbursementsModalOpen}
          />
        )}
      </>
    )
  } else {
    return (
      <div className="flex flex-wrap flex-row-reverse w3">
        {reimbursement.fileGuids.map((guid) => (
          <FileDrawingLink key={guid} guid={guid} />
        ))}
        {[...Array(reimbursement.filesPendingScanCount)].map((x, i) => (
          <FilePendingScan key={`pendingFile-${i}`} />
        ))}
      </div>
    )
  }
}

const ExpenseTypeIcon = ({ isCardExpense }) => (
  <MultiPathSVG iconName={isCardExpense ? "badge-transaction-card" : "badge-transaction-oop"} />
)

const TableHeader = () => (
  <>
    <Header>
      <FormattedMessage defaultMessage="Type" description="the category" />
    </Header>
    <Header>
      <FormattedMessage defaultMessage="Submitted on" description="the date the expense was presented" />
    </Header>
    <Header rightAlign="true">
      <FormattedMessage defaultMessage="Uploads" description="files that have been submitted" />
    </Header>
  </>
)

const Row = ({ reimbursement }) => (
  <tr {...getPiiProps()} className="bt bw1 b--black-05">
    <td className="pv3 v-mid" style={{ width: "3em" }}>
      <ExpenseTypeIcon isCardExpense={reimbursement.isCardExpense} />
    </td>
    <td className="pv3 v-mid">
      <Details reimbursement={reimbursement} />
    </td>
    <td className="pv3">
      <div className="fr">
        {reimbursement.isBenefitTransfer ? (
          <BodySmall color="black-60">{l`client.reimbursements.benefitTransfer`}</BodySmall>
        ) : (
          <Attachments reimbursement={reimbursement} />
        )}
      </div>
    </td>
  </tr>
)

const Details = ({ reimbursement }) => {
  const dateStringToDate = moment(reimbursement.submittedDatetime).toDate()
  return (
    <div>
      <div className="flex flex-row">
        <div className="flex-column mr3">
          <H5>
            <FormattedDate value={dateStringToDate} year="numeric" month="short" day="2-digit" />
          </H5>
        </div>
        <ReimbursementAmount reimbursement={reimbursement} />
      </div>
      <AdjustmentDetails reimbursement={reimbursement} />
      <BodySmall color="black-60">{reimbursement.merchantName}</BodySmall>
      <Text size="small" variant="accent">
        <RenderStatus reimbursement={reimbursement} />
      </Text>
    </div>
  )
}

const AdjustmentDetails = ({ reimbursement }) => {
  const [isAdjustmentModalOpen, toggleIsAdjustmentsModalOpen] = useToggle(false)
  const intl = useIntl()
  const AdjustmentInfoModal = () => (
    <EmployeeOnlyModalContainer onClose={toggleIsAdjustmentsModalOpen}>
      <Modal
        titleText={intl.formatMessage({
          defaultMessage: "What does adjusted mean?"
        })}
        onExit={toggleIsAdjustmentsModalOpen}
        underlayClickExits={true}
      >
        <Container paddingLeftRight="huge" alignItems="center">
          <H2>
            <FormattedMessage defaultMessage="What does adjusted mean?" />
          </H2>
          <Spacer height={1.5} />
          <BodySmall>
            <FormattedMessage defaultMessage="We needed to make a change to your expense after we processed it. Reach out to your Care Team with any questions." />
          </BodySmall>
          <Spacer height={3} />
        </Container>
      </Modal>
    </EmployeeOnlyModalContainer>
  )
  const dateStringToDate = reimbursement.adjustmentDatetime ? moment(reimbursement.adjustmentDatetime).toDate() : null
  const formattedDate = dateStringToDate
    ? intl.formatDate(dateStringToDate, {
        month: "short",
        day: "2-digit"
      })
    : null
  return reimbursement.adjustmentDatetime ? (
    <>
      <FlexContainer alignItems="center">
        <BodySmall color="black-60">
          <span id="adjustment-date">
            <FormattedMessage defaultMessage="Adjusted on {formattedDate}" values={{ formattedDate }} />
          </span>
        </BodySmall>
        <button
          id="btn-adjustments-more-info"
          style={{ margin: "0 4px", border: "none", display: "flex", borderRadius: "100%", padding: "4px" }}
          className="pointer bg-white hover-bg-very-light-carrot-orange"
          aria-label={intl.formatMessage({
            defaultMessage: "Learn more about adjusted expense"
          })}
          onClick={() => toggleIsAdjustmentsModalOpen()}
        >
          <Icon name="helpCircle" color="carrotOrange" size="15px" />
        </button>
      </FlexContainer>
      {isAdjustmentModalOpen && <AdjustmentInfoModal />}
    </>
  ) : null
}

const RenderStatus = ({ reimbursement }) => {
  const intl = useIntl()
  const submitByDate = new Date(reimbursement.submittedDatetime)
  const dateStringToDate = moment(submitByDate).toDate()
  const formattedDate = intl.formatDate(dateStringToDate, {
    month: "short",
    day: "2-digit"
  })
  submitByDate.setDate(submitByDate.getDate() + 30)

  if (reimbursement.isCardExpense) {
    switch (reimbursement.cardStatus) {
      case CardExpenseStatus.PENDING_MEMBER_INFO:
        return (
          <FormattedMessage
            defaultMessage="Please upload an itemized statement by {formattedDate}"
            values={{ formattedDate }}
          />
        )
      case CardExpenseStatus.APPROVED:
        return <FormattedMessage defaultMessage="Approved" />
      case CardExpenseStatus.DENIED_RECONCILED:
        return <FormattedMessage defaultMessage="Denied" />
      case CardExpenseStatus.DENIED_RECONCILIATION_REQUESTED:
        return (
          <>
            <span>
              <FormattedMessage
                defaultMessage="Needs more info, please <link>contact us</link>"
                values={{
                  link: (linkContent) => <TextLink to="mailto:support@get-carrot.com">{linkContent}</TextLink>
                }}
              />
            </span>
          </>
        )
      case CardExpenseStatus.PENDING_CARE_NAV:
        return <FormattedMessage defaultMessage="In progress" />
      default:
        reportErrorMessage(`unexpected status provided: ${reimbursement.cardStatus}`)
        return reimbursement.cardStatus
    }
  } else {
    switch (reimbursement.status) {
      case ExpenseStatus.RECEIVED:
        return <FormattedMessage defaultMessage="In progress" />
      case ExpenseStatus.APPROVED:
        return (
          <>
            <FormattedMessage defaultMessage="Approved" />
            <ReimbursementsListDeductibleString reimbursement={reimbursement} />
          </>
        )
      case ExpenseStatus.PAID:
        return <ReimbursementsListPaidStatus reimbursement={reimbursement} />
      case ExpenseStatus.DENIED:
        return <FormattedMessage defaultMessage="Denied" />
      default:
        reportErrorMessage(`unexpected status provided: ${reimbursement.status}`)
        return reimbursement.status
    }
  }
}

const ReimbursementAmount = ({ reimbursement }) => {
  const [isAmountsModalOpen, toggleIsAmountsModalOpen] = useToggle(false)
  const intl = useIntl()
  const AmountsInfoModal = () => (
    <EmployeeOnlyModalContainer onClose={toggleIsAmountsModalOpen}>
      <Modal
        titleText={intl.formatMessage({
          defaultMessage: "What is this amount?"
        })}
        onExit={toggleIsAmountsModalOpen}
        underlayClickExits={true}
      >
        <Container paddingLeftRight="huge" alignItems="center">
          <H2>
            <FormattedMessage defaultMessage="What is this amount?" />
          </H2>
          <Spacer height={1.5} />
          <BodySmall>
            <FormattedMessage defaultMessage="The number in parentheses is the currency you paid in." />
          </BodySmall>
          <Spacer height={3} />
        </Container>
      </Modal>
    </EmployeeOnlyModalContainer>
  )

  const displayAdjustedAmount =
    reimbursement.adjustmentDatetime &&
    reimbursement.originalAmount &&
    reimbursement.originalAmount !== reimbursement.amount

  const displayAmountInMerchantCurrency = reimbursement.currencyCode !== reimbursement.merchantCurrency
  const companyCurrencyCode = useSelector(getCompanyCurrencyCode)
  const pendingDisplayAmount = intl
    .formatNumber(0.0, {
      style: "currency",
      currency: companyCurrencyCode,
      currencyDisplay: "narrowSymbol"
    })
    .replaceAll("0", "-")
  return !reimbursement.isCardExpense && reimbursement.status === ExpenseStatus.RECEIVED ? (
    <H5>{pendingDisplayAmount}</H5>
  ) : (
    <>
      <div className="flex-column">
        <H5>
          {displayAdjustedAmount ? (
            <>
              <span id="original-amount" style={{ fontWeight: "normal", textDecoration: "line-through" }}>
                <FormattedNumber
                  value={reimbursement.originalAmount / 100}
                  style="currency"
                  currency={reimbursement.currencyCode}
                  currencyDisplay="narrowSymbol"
                />
              </span>
              {displayAmountInMerchantCurrency ? (
                <span
                  id="original-amount-in-merchant-currency"
                  className="black-60"
                  style={{ fontWeight: "normal", textDecoration: "line-through" }}
                >
                  &nbsp;(
                  <FormattedNumber
                    value={reimbursement.originalAmountInMerchantCurrency / 100}
                    style="currency"
                    currency={reimbursement.merchantCurrency}
                    currencyDisplay="narrowSymbol"
                  />
                  )
                </span>
              ) : null}
              <Icon name="rightArrow" color="rgba(0, 0, 0, 0.4)" size="10px" style={{ margin: "0 8px" }} />
            </>
          ) : null}
          <span id="amount">
            <FormattedNumber
              value={reimbursement.amount / 100}
              style="currency"
              currency={reimbursement.currencyCode}
              currencyDisplay="narrowSymbol"
            />
          </span>
          {displayAmountInMerchantCurrency ? (
            <span id="amount-in-merchant-currency" className="black-60">
              &nbsp;(
              <FormattedNumber
                value={reimbursement.amountInMerchantCurrency / 100}
                style="currency"
                currency={reimbursement.merchantCurrency}
                currencyDisplay="narrowSymbol"
              />
              )
            </span>
          ) : null}
        </H5>
      </div>
      {displayAmountInMerchantCurrency ? (
        <div className="flex-column">
          <button
            id="btn-merchant-amount-more-info"
            style={{
              margin: "0 4px",
              border: "none",
              display: "flex",
              borderRadius: "100%",
              padding: "4px"
            }}
            className="pointer bg-white hover-bg-light-gray"
            aria-label={intl.formatMessage({
              defaultMessage: "Learn more about these amounts"
            })}
            onClick={() => toggleIsAmountsModalOpen()}
          >
            <Icon name="helpCircle" className="black-60" size="12px" />
          </button>
        </div>
      ) : null}
      {isAmountsModalOpen && <AmountsInfoModal />}
    </>
  )
}

const ReimbursementsListSection = ({ title, items, emptyText }) => {
  const hasItems = items && items.length > 0
  return (
    <div>
      <Spacer height={1} />
      <H4 variant="primary">{title}</H4>
      <Spacer height={1} />
      <table className="w-100 collapse">
        <thead className={hasItems ? "" : "o-40"}>
          <tr>
            <TableHeader />
          </tr>
        </thead>
        <tbody>
          {hasItems ? (
            items.map((item) => <Row key={item.expenseId} reimbursement={item} />)
          ) : (
            <tr>
              <td colSpan="3" className="bt bw1 b--black-05 pv3">
                <BodySmall color="black-60 tc">{emptyText}</BodySmall>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

const EmptyReimbursementsList = () => {
  const isCardEligible = useSelector(getCardState) !== CardStates.INELIGIBLE
  return (
    <div>
      <Spacer height={1} />
      <table className="w-100 collapse">
        <thead className="o-40">
          <tr>
            <TableHeader />
          </tr>
        </thead>
        <tbody>
          <tr className="bt bw1 b--black-05">
            <td colSpan="3" className="pv3">
              <div className="w-100 flex justify-center pv5">
                <div className="flex flex-column items-center" style={{ maxWidth: "360px" }}>
                  <CircleImage iconName="icn-transaction" size={84} padding={17} backgroundColor="nurse-gray" />
                  <Spacer height={1.5} />
                  <BodySmall align="tc" color="black-60">
                    {isCardEligible ? (
                      <FormattedMessage defaultMessage="Your Carrot Card® transactions and reimbursement requests will appear here." />
                    ) : (
                      <FormattedMessage defaultMessage="Your reimbursement requests will appear here." />
                    )}
                  </BodySmall>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

const ReimbursementSummary = ({ subsidy, reimbursementsCount }) => {
  const intl = useIntl()
  const isBenefitLifetimeOnly = subsidy.yearlyAmounts.max === subsidy.lifetimeAmounts.max
  const hasUnlimitedBenefit = subsidy.hasUnlimitedBenefit
  const lifetimeAvailable = subsidy.lifetimeAmounts.available
  const yearlyAvailable = subsidy.yearlyAmounts.available
  const used = subsidy.lifetimeAmounts.claimed
  const showRemainingAnnual = !isBenefitLifetimeOnly && !hasUnlimitedBenefit
  const showRemainingLifetime = !hasUnlimitedBenefit && lifetimeAvailable !== null
  const companyCurrencyCode = useSelector(getCompanyCurrencyCode)

  return (
    <ReimbursementDescriptionList>
      {showRemainingAnnual && (
        <ReimbursementDescriptionGroup
          title={intl.formatMessage({
            defaultMessage: "Remaining annual",
            description: "the remaining amount of money allocated for the year"
          })}
          value={intl.formatNumber(Math.max(yearlyAvailable / 100, 0), {
            style: "currency",
            currency: companyCurrencyCode,
            currencyDisplay: "narrowSymbol",
            maximumFractionDigits: 0
          })}
        />
      )}
      {showRemainingLifetime && (
        <ReimbursementDescriptionGroup
          title={intl.formatMessage({
            defaultMessage: "Remaining lifetime",
            description: "the remaining amount of money allocated for lifetime"
          })}
          value={intl.formatNumber(Math.max(lifetimeAvailable / 100, 0), {
            style: "currency",
            currency: companyCurrencyCode,
            currencyDisplay: "narrowSymbol",
            maximumFractionDigits: 0
          })}
        />
      )}
      <ReimbursementDescriptionGroup
        title={intl.formatMessage({
          defaultMessage: "used",
          description: "money spent"
        })}
        value={intl.formatNumber(Math.max(used / 100, 0), {
          style: "currency",
          currency: companyCurrencyCode,
          currencyDisplay: "narrowSymbol",
          maximumFractionDigits: 0
        })}
      />
      <ReimbursementDescriptionGroup
        title={intl.formatMessage({ defaultMessage: "# of expenses", description: "number of expenses" })}
        value={intl.formatNumber(reimbursementsCount)}
      />
    </ReimbursementDescriptionList>
  )
}

const SingleCategoryReimbursementsList = ({ reimbursementsList, subsidy }) => {
  const intl = useIntl()
  const reimbursementsRequiringAction = getReimbursementsRequiringActionList(reimbursementsList)
  const reimbursementsPending = getPendingReimbursementsList(reimbursementsList)
  const reimbursementsCompleted = getCompletedReimbursementsList(reimbursementsList)

  return (
    <>
      {subsidy.hasUnlimitedBenefit || (
        <>
          <H2 variant="primary" stack="tiny">
            <FormattedMessage defaultMessage="Available funds" description="money available to be spent" />
          </H2>
          <ReimbursementSummary
            subsidy={subsidy}
            reimbursementsCount={reimbursementsList ? reimbursementsList.length : 0}
          />
          <PlanInfoBadgeContainer
            backgroundColor={"black-10"}
            fontColor={"black-80"}
            subsidy={subsidy}
            categorySupportsDeductibles={true}
          />
          <Spacer height={3} />
        </>
      )}
      {reimbursementsList.length > 0 ? (
        <>
          <H2 variant="primary">
            <FormattedMessage defaultMessage="Activity" description="things that have been done" />
          </H2>
          <Spacer height={0.5} />
          <Legend />
          <ReimbursementsListSection
            title={
              <Badge badgeContent={intl.formatNumber(reimbursementsRequiringAction.length)} color={"error"}>
                <FormattedMessage defaultMessage="Needs Attention" />
              </Badge>
            }
            emptyText={intl.formatMessage({ defaultMessage: "No transactions need attention" })}
            items={reimbursementsRequiringAction}
          />
          <ReimbursementsListSection
            title={intl.formatMessage({ defaultMessage: "Pending" })}
            emptyText={intl.formatMessage({ defaultMessage: "No pending transactions" })}
            items={reimbursementsPending}
          />
          <ReimbursementsListSection
            title={intl.formatMessage({ defaultMessage: "Complete" })}
            emptyText={intl.formatMessage({ defaultMessage: "No completed transactions" })}
            items={reimbursementsCompleted}
          />
        </>
      ) : (
        <EmptyReimbursementsList />
      )}
    </>
  )
}

const WaitingReimbursementList = ({ reimbursements }) => {
  const reimbursementsRequiringAction = getReimbursementsRequiringActionList(reimbursements)
  const receivedReimbursements = getReceivedReimbursementsList(reimbursements)
  return (
    <CategoryHeader>
      <CategoryIconColumn>
        <CircleImage iconName={"icn-expenses-pending"} size={36} padding={6} backgroundColor={"mint"} />
        {reimbursementsRequiringAction.length > 0 && (
          <FlexContainer
            width="10px"
            justifyContent="center"
            alignItems="center"
            sx={{ marginTop: "-36px", marginLeft: "27px" }}
          >
            <Icon name="dot" color="errorRed" size="tiny" />
          </FlexContainer>
        )}
      </CategoryIconColumn>
      <CategoryAccordionColumn>
        <AccordionWithoutMarkdown
          title={
            <>
              <H3 stack="tiny">Waiting to be processed</H3>
              <ReimbursementDescriptionList columns={[1, null, 3]}>
                <ReimbursementDescriptionGroup
                  title={l`client.reimbursements.numberOfExpenses`}
                  value={reimbursementsRequiringAction.length + receivedReimbursements.length}
                />
                <ReimbursementDescriptionGroup
                  title={
                    <span style={{ whiteSpace: "nowrap" }}>
                      Need your attention
                      {reimbursementsRequiringAction.length > 0 && (
                        <Icon sx={{ position: "relative", top: "-8px" }} name="dot" color="errorRed" size="5px" />
                      )}
                    </span>
                  }
                  value={reimbursementsRequiringAction.length}
                />
              </ReimbursementDescriptionList>
            </>
          }
          key="Waiting to be processed"
          shouldExpand={reimbursementsRequiringAction.length > 0}
          chevronOnRight={true}
          chevronHeight={17}
          disabled={reimbursementsRequiringAction.length + receivedReimbursements.length === 0}
        >
          <Spacer height={2} />
          <Legend />
          {reimbursementsRequiringAction.length > 0 && (
            <ReimbursementsListSection
              title={
                <Badge badgeContent={reimbursementsRequiringAction.length} color={"error"}>
                  {l`client.reimbursements.needsAttention`}
                </Badge>
              }
              emptyText={l`client.reimbursements.needsAttentionEmpty`}
              items={reimbursementsRequiringAction}
            />
          )}
          {receivedReimbursements.length > 0 && (
            <ReimbursementsListSection
              title={l`client.reimbursements.received`}
              emptyText={l`client.reimbursements.receivedEmpty`}
              items={receivedReimbursements}
            />
          )}
        </AccordionWithoutMarkdown>
      </CategoryAccordionColumn>
    </CategoryHeader>
  )
}

const CategoryReimbursementList = ({ reimbursements, subsidy, categorySupportsDeductibles }) => {
  const reimbursementsPending = getCategoryPendingReimbursementsList(reimbursements)
  const reimbursementsCompleted = getCompletedReimbursementsList(reimbursements)
  const intl = useIntl()
  const categoryName = intl.formatMessage(
    reimbursementCategoryNameMessages[subsidy.reimbursementCategoryNameMessageKey]
  )
  return (
    <>
      <CategoryDivider />
      <Spacer height={1} />
      <CategoryHeader>
        <CategoryIconColumn>
          <RetinaImage name={subsidy.reimbursementCategoryIconName || "icn-expenses-other"} height={36} width={36} />
        </CategoryIconColumn>
        <CategoryAccordionColumn>
          <AccordionWithoutMarkdown
            title={
              <>
                <CategoryTitle>
                  <H3>
                    <FormattedMessage
                      {...reimbursementCategoryNameMessages[subsidy.reimbursementCategoryNameMessageKey]}
                    />
                  </H3>
                  <div
                    onClick={(event) => {
                      event.stopPropagation()
                    }}
                  >
                    <PlanInfoBadgeContainer
                      backgroundColor={"black-10"}
                      fontColor={"black-80"}
                      subsidy={subsidy}
                      categorySupportsDeductibles={categorySupportsDeductibles}
                    />
                  </div>
                </CategoryTitle>
                <Spacer height={1} />
                <ReimbursementSummary
                  subsidy={subsidy}
                  reimbursementsCount={reimbursementsPending.length + reimbursementsCompleted.length}
                />
              </>
            }
            key={categoryName}
            chevronOnRight={true}
            chevronHeight={17}
            disabled={reimbursementsPending.length + reimbursementsCompleted.length === 0}
          >
            <Spacer height={2} />
            <Legend />
            {reimbursementsPending.length > 0 && (
              <ReimbursementsListSection title={"Pending"} emptyText="" items={reimbursementsPending} />
            )}
            {reimbursementsCompleted.length > 0 && (
              <ReimbursementsListSection title={"Completed"} emptyText="" items={reimbursementsCompleted} />
            )}
          </AccordionWithoutMarkdown>
        </CategoryAccordionColumn>
      </CategoryHeader>
    </>
  )
}

const MultipleCategoriesReimbursementsLists = ({ reimbursementsList, subsidies }) => {
  const hasQmeCategory = subsidies?.some(
    (s) => s.reimbursementCategoryNameMessageKey === reimbursementCategoryNameMessageKeys.QME
  )
  const intl = useIntl()
  return (
    <div {...getPiiProps()}>
      <Body color={"black-60"} weight={350}>
        <FormattedMessage defaultMessage="Your employer offers separate fund categories for different kinds of eligible care and services. Funds from one category cannot be transferred to another." />
        <Spacer height={1} />
        <FormattedMessage
          defaultMessage="Check your <link>benefit guide</link> to learn what's covered."
          values={{
            link: (linkContent) => (
              <TextLink
                to={DbgPaths.PAYING_FOR_CARE}
                className={"bb b--light-carrot-orange no-underline pointer cc dim animate-all lh-copy"}
              >
                {linkContent}
              </TextLink>
            )
          }}
        />
      </Body>
      <Spacer height={1.5} />
      <WaitingReimbursementList reimbursements={reimbursementsList} />
      {subsidies.map((subsidy) => {
        const reimbursements = reimbursementsList.filter(
          (r) => r.expensePlanCategoryNameMessageKey === subsidy.reimbursementCategoryNameMessageKey
        )
        const categoryNameMessageKey = subsidy.reimbursementCategoryNameMessageKey
        const categoryName = intl.formatMessage(reimbursementCategoryNameMessages[categoryNameMessageKey])
        const categorySupportsDeductibles =
          categoryNameMessageKey !== reimbursementCategoryNameMessageKeys.ADOPTION &&
          categoryNameMessageKey !== reimbursementCategoryNameMessageKeys.GC &&
          categoryNameMessageKey !== reimbursementCategoryNameMessageKeys.ADOPTION_AND_GC &&
          (categoryNameMessageKey !== reimbursementCategoryNameMessageKeys.QME || !hasQmeCategory)
        return (
          <CategoryReimbursementList
            reimbursements={reimbursements}
            subsidy={subsidy}
            categorySupportsDeductibles={categorySupportsDeductibles}
            key={categoryName}
          />
        )
      })}
    </div>
  )
}

const ReimbursementsList = () => {
  const reimbursements = useSelector((state) => ({
    reimbursementsList: getReimbursementsList(state)
  }))

  const { reimbursementsList } = reimbursements

  const subsidies = useSelector(getSubsidies)
  const hasMultipleCategories = subsidies?.length > 1

  return (
    <div>
      {subsidies &&
        reimbursements &&
        (hasMultipleCategories ? (
          <MultipleCategoriesReimbursementsLists reimbursementsList={reimbursementsList} subsidies={subsidies} />
        ) : (
          <SingleCategoryReimbursementsList reimbursementsList={reimbursementsList} subsidy={subsidies[0]} />
        ))}
    </div>
  )
}

export default ReimbursementsList
